import clsx from 'clsx';
import React from 'react';
import Button from '../Button';
import ChevronLeftIcon from '../../assets/icons/chevron-down-icon.svg?react';
import {
  CorrectiveActionsPriorityEnum,
  GetCorrectiveActionAssignedUsingIdResponse,
} from '@EHSNavigator/backend';
import ChevronIcon from '../../assets/icons/chevron-down-icon.svg?react';
import DatePicker from '../DatePicker';
import moment from 'moment';
import TimePicker from '../TimePicker';
import Input from '../Input';
import TextLeftIcon from '../../assets/icons/text-left-icon.svg?react';
import WarningIcon from '../../assets/icons/warning-icon.svg?react';
import Select from '../Select';
import MultiAssigneeDropDown from '../MultiAssigneeDropDown';
import TextArea from '../TextArea';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import AssignedCorrectiveActionsDetailBanner from '../AssignedCorrectiveActionsDetailBanner';

interface AssignedCorrectiveActionsDetailStepOneProps {
  otherClasses?: string;
  handleSteps: React.Dispatch<number>;
  formHandler: any;
  data: GetCorrectiveActionAssignedUsingIdResponse['correctiveAction'];
  departments: { label: string; value: string }[];
  userList: { label: string; value: string }[];
  isUnMatchValues: boolean;
}

const AssignedCorrectiveActionsDetailStepOne: React.FC<
  AssignedCorrectiveActionsDetailStepOneProps
> = ({
  otherClasses,
  handleSteps,
  formHandler,
  data,
  departments,
  userList,
  isUnMatchValues,
}) => {
  const assignedCorrectiveActionsDetailStepOneClasses = clsx(otherClasses);

  const filteredUserList = userList.filter((user) =>
    formHandler.values.assignee.includes(user.value),
  );

  let updatedDepartments = [...departments];
  const exists = departments?.some(
    (node) => node.value === data?.department?.id,
  );
  if (!exists && data?.department?.id) {
    updatedDepartments.push({
      label: data?.department?.name || '',
      value: data?.department?.id,
    });
  }
  return (
    <form
      action="#"
      onSubmit={formHandler?.handleSubmit}
      className={assignedCorrectiveActionsDetailStepOneClasses}
      data-testid="assigned-corrective-actions-detail-step-one"
    >
      <div className="flex flex-col items-start gap-2.5">
        <Button
          type="button"
          onClick={() => handleSteps(1)}
          variant="link"
          otherClasses="gap-2.5 w-fit "
        >
          <span className="bg-brand-byzantine rounded-5 flex h-8 w-8 items-center justify-center">
            <ChevronLeftIcon className="h-7 w-7 rotate-90 [&>path]:fill-white" />
          </span>
          <strong>Return</strong>
        </Button>

        <h1 className="h3 mb-1 lg:mb-0">Edit Corrective Action</h1>
        <p className="text-brand-byzantine font-inter-variable -tracking-button text-base leading-5 font-bold lg:text-xl lg:leading-6">
          STEP 1 / 2
        </p>
      </div>
      <AssignedCorrectiveActionsDetailBanner data={data} />
      <div className="flex w-full flex-wrap items-start gap-x-12 gap-y-9">
        <div className="flex flex-col gap-6">
          <h6 className="font-inter-variable text-brand-byzantine -tracking-button text-lg leading-5 font-bold lg:text-xl lg:leading-6">
            Due Date & Time
          </h6>
          <div className="flex flex-col items-start gap-9 md:flex-row">
            <DatePicker
              otherClasses={{ container: '!w-235 lg:!w-235 lg:max-w-235 ' }}
              label="Date*"
              name="date"
              value={formHandler?.values?.date}
              placeholder="Select Date"
              onBlur={() => {
                if (formHandler?.values?.date) {
                  formHandler?.setFieldTouched('date', true);
                }
              }}
              disabled={
                formHandler.isSubmitting ||
                formHandler?.values?.dateTimeUndetermined
              }
              startMonth={moment().toDate()}
              endMonth={undefined}
              onChange={(e) => formHandler?.setFieldValue('date', e, true)}
              errorMessage={
                formHandler?.touched.date && formHandler?.errors.date
                  ? formHandler?.errors.date
                  : ''
              }
            />
            <TimePicker
              onBlur={formHandler?.handleBlur}
              label="Time*"
              errorMessage={
                formHandler?.touched.time && formHandler?.errors.time
                  ? formHandler?.errors.time
                  : ''
              }
              disabled={
                formHandler?.isSubmitting ||
                formHandler?.values?.dateTimeUndetermined
              }
              value={formHandler?.values.time}
              placeholder="Select Time"
              onChange={formHandler?.handleChange}
              name="time"
              otherClasses={{ container: '!w-235 lg:!w-235 lg:max-w-235 ' }}
            />
          </div>
        </div>
        <div className="flex w-full flex-col gap-6 lg:w-auto">
          <h6 className="font-inter-variable text-brand-byzantine -tracking-button text-lg leading-5 font-bold lg:text-xl lg:leading-6">
            Assignee
          </h6>
          <div className="flex flex-col items-start gap-x-9 gap-y-6 md:flex-row">
            <Select
              label="Department*"
              list={updatedDepartments}
              value={formHandler.values.department}
              disabled={formHandler.isSubmitting}
              placeholder="Departments"
              otherClasses={{ container: 'w-full lg:max-w-261 lg:w-261' }}
              onSelect={(e) =>
                formHandler?.setFieldValue('department', e, true)
              }
              errorMessage={
                formHandler?.touched.department &&
                formHandler?.errors.department
                  ? formHandler?.errors.department
                  : ''
              }
            />

            <MultiAssigneeDropDown
              label="Assignee*"
              withSearch
              disabled={formHandler.isSubmitting}
              list={userList}
              placeholder="Select Assignee"
              value={formHandler.values.assignee}
              onSelect={(e) => {
                const { assignee = [] } = formHandler?.values || {};
                const updatedAssignees = assignee?.includes(e)
                  ? assignee?.filter((item: any) => item !== e)
                  : [...assignee, e];

                formHandler?.setFieldValue('assignee', updatedAssignees, true);
                if (e === formHandler?.values?.leadAssignee) {
                  formHandler?.setFieldValue('leadAssignee', '', true);
                }
              }}
              handleRemove={(e) => {
                const { assignee = [] } = formHandler?.values || {};
                const updatedAssignees = assignee?.filter(
                  (item: any) => item !== e,
                );
                formHandler?.setFieldValue('assignee', updatedAssignees, true);
                if (e === formHandler?.values?.leadAssignee) {
                  formHandler?.setFieldValue('leadAssignee', '', true);
                }
              }}
              errorMessage={
                formHandler?.touched.assignee && formHandler?.errors.assignee
                  ? formHandler?.errors.assignee
                  : ''
              }
              otherClasses={{ container: 'w-full lg:max-w-261 lg:w-261' }}
            />
          </div>
          {formHandler.values.assignee?.length >= 2 && (
            <div>
              <MultiAssigneeDropDown
                label={
                  <p
                    className={clsx(
                      'text-form-label -tracking-form-label font-inter-variable flex items-center gap-2 font-bold',
                      formHandler.isSubmitting
                        ? `text-neutral-tones-text/35`
                        : `text-neutral-tones-text`,
                    )}
                  >
                    Lead Assignee*{' '}
                    <button
                      type="button"
                      className="group relative cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <WarningIcon
                        className={clsx(
                          '[&>g>path]:fill-brand-byzantine h-4 w-4',
                        )}
                      />
                      <span className="bg-neutral-tones-sidebar text-neutral-tones-text absolute bottom-6 left-2/4 z-10 hidden min-w-211 -translate-x-2/4 items-center justify-center rounded px-2 py-1 text-center text-xs font-normal shadow-lg group-hover:block group-focus:block">
                        Primary assignee responsible for overseeing corrective
                        actions. Can request due date extension and send
                        corrective action for review
                      </span>
                    </button>
                  </p>
                }
                disabled={formHandler.isSubmitting}
                list={filteredUserList}
                placeholder="Select Lead Assignee"
                value={formHandler.values.leadAssignee}
                onSelect={(e) => {
                  formHandler?.setFieldValue('leadAssignee', e, true);
                }}
                handleRemove={() => {
                  formHandler?.setFieldValue('leadAssignee', '', true);
                }}
                singleSelect={true}
                errorMessage={
                  formHandler?.touched.leadAssignee &&
                  formHandler?.errors.leadAssignee
                    ? formHandler?.errors.leadAssignee
                    : ''
                }
                otherClasses={{ container: 'w-full' }}
              />
            </div>
          )}
        </div>
      </div>
      <div className="mt-9 flex w-full flex-col items-start gap-x-9 gap-y-9 md:flex-row lg:flex-wrap">
        <div className="flex w-full flex-col gap-6 md:w-2/4 lg:w-auto">
          <h6 className="font-inter-variable text-brand-byzantine -tracking-button text-lg leading-5 font-bold lg:text-xl lg:leading-6">
            Action Priority*
          </h6>
          <Select
            list={Object.values(CorrectiveActionsPriorityEnum).map(
              (status) => ({
                label: status,
                value: status,
              }),
            )}
            value={formHandler.values.actionPriority}
            disabled={formHandler.isSubmitting}
            placeholder="Priorities"
            otherClasses={{ container: 'w-full lg:max-w-465 lg:w-465' }}
            onSelect={(e) =>
              formHandler?.setFieldValue('actionPriority', e, true)
            }
            errorMessage={
              formHandler?.touched.actionPriority &&
              formHandler?.errors.actionPriority
                ? formHandler?.errors.actionPriority
                : ''
            }
          />
        </div>
        <div className="flex w-full flex-col gap-6 md:w-2/4 lg:w-auto">
          <h6 className="font-inter-variable text-brand-byzantine -tracking-button text-lg leading-5 font-bold lg:text-xl lg:leading-6">
            Action Title*
          </h6>
          <div className="flex flex-col items-start gap-x-9 gap-y-6 md:flex-row">
            <Input
              onBlur={formHandler?.handleBlur}
              errorMessage={
                formHandler?.touched.actionTitle &&
                formHandler?.errors.actionTitle
                  ? formHandler?.errors.actionTitle
                  : ''
              }
              disabled={
                formHandler?.isSubmitting ||
                formHandler?.values?.dateTimeUndetermined
              }
              value={formHandler?.values.actionTitle}
              type="text"
              placeholder="Enter action title"
              onChange={formHandler?.handleChange}
              name="actionTitle"
              icon={
                <TextLeftIcon
                  className={clsx('[&>path]:stroke-neutral-tones-text/50')}
                />
              }
              otherClasses={{
                container: 'w-full lg:max-w-465 lg:w-465',
                input: 'pl-14',
                icon: 'left-5 right-auto',
              }}
            />
          </div>
        </div>
      </div>
      <div className="mt-9 mb-6 flex w-full flex-col items-start gap-x-9 gap-y-9 md:flex-row lg:my-9 lg:flex-wrap">
        <div className="flex w-full flex-col gap-6 lg:w-auto">
          <h6 className="font-inter-variable text-brand-byzantine -tracking-button text-lg leading-5 font-bold lg:text-xl lg:leading-6">
            Description of Action
          </h6>
          <div className="flex flex-col items-start gap-x-9 gap-y-6 md:flex-row">
            <TextArea
              name="description"
              onBlur={formHandler?.handleBlur}
              onChange={formHandler?.handleChange}
              value={formHandler?.values?.description}
              placeholder="Write something here e.g. The chemical spill must be cleaned up..."
              disabled={formHandler?.isSubmitting}
              errorMessage={
                formHandler?.touched.description &&
                formHandler?.errors.description
                  ? formHandler?.errors.description
                  : ''
              }
              label="Description*"
              otherClasses={{ container: 'w-full lg:max-w-465 lg:w-465' }}
            />
          </div>
        </div>
      </div>
      <Button
        onClick={() => handleSteps(3)}
        otherClasses="w-full max-w-185"
        size={useButtonSizeLarge()}
        disable={isUnMatchValues}
      >
        Next
        <ChevronIcon className="h-5 w-5 -rotate-90 [&>path]:fill-current" />
      </Button>
    </form>
  );
};

export default AssignedCorrectiveActionsDetailStepOne;
